// =============================================================================
// Transport ratesheet + price calculation
//
// Mirrors the Mount Sinai ratesheet managed in TripLink. Each trip is
// priced as:
//
//   window   = pickup time in 03:00–18:59 ? "day" : "night"
//   base     = rate(serviceType, window, "base").amountCents
//   perMile  = rate(serviceType, window, "per_mile").amountCents
//   billable = max(0, mileage − freeMiles)        // freeMiles = 15
//   total    = base + round(perMile × billable)
//
// `RATE_SHEET` below is the single source of truth for the seed values.
// The API seeds these rows into the `transport_rates` table and loads
// them back (so they could one day be edited), but the math always runs
// through `computeTripCharges` so the UI, CSV export, and any future
// reporting agree to the cent.
//
// IMPORTANT: amounts are integer CENTS — never floats — to keep money
// math exact.
// =============================================================================

/** Time-of-day pricing window. */
export type RateWindow = "day" | "night";

/** A ratesheet line is either a flat per-trip base or a per-mile rate. */
export type RateType = "base" | "per_mile";

/** Day window boundaries (inclusive) in minutes-since-midnight. The
 *  night window is simply everything outside this. Matches the
 *  ratesheet's 03:00–18:59 / 19:00–02:59 split. */
const DAY_START_MIN = 3 * 60; // 03:00
const DAY_END_EXCLUSIVE_MIN = 19 * 60; // 19:00 (night starts here)

/** Miles included in the base before per-mile charges begin ("Per Mile
 *  Start From" on the ratesheet). */
export const FREE_MILES = 15;

export interface RateRow {
  /** Our transport code: AMB | W/C | BWC | STR | BSTR. */
  transportType: string;
  rateType: RateType;
  window: RateWindow;
  /** "HH:MM" inclusive window bounds — stored for fidelity with the
   *  source ratesheet and the DB seed. The live day/night decision uses
   *  the canonical boundaries above, not these strings. */
  windowStart: string;
  windowEnd: string;
  amountCents: number;
  /** Only meaningful for `per_mile` rows. */
  freeMiles: number;
}

const DAY = { windowStart: "03:00", windowEnd: "18:59" } as const;
const NIGHT = { windowStart: "19:00", windowEnd: "02:59" } as const;

/** Build the two rows (day + night) for one service type + rate type. */
function pair(
  transportType: string,
  rateType: RateType,
  dayCents: number,
  nightCents: number,
  freeMiles = 0,
): RateRow[] {
  return [
    {
      transportType,
      rateType,
      window: "day",
      ...DAY,
      amountCents: dayCents,
      freeMiles,
    },
    {
      transportType,
      rateType,
      window: "night",
      ...NIGHT,
      amountCents: nightCents,
      freeMiles,
    },
  ];
}

/**
 * The seeded Mount Sinai ratesheet (cents). Base + per-mile, day + night,
 * per service type. Values come from the signed contract — Exhibit A,
 * Rate Book, MSH 1/12/2026 ("standard hours" = our day window,
 * "after hours 7:00pm–3:00AM" = our night window). An earlier
 * transcription from TripLink screenshots had several day/night pairs
 * swapped; the contract is authoritative.
 */
export const RATE_SHEET: RateRow[] = [
  // ---- Base (flat per trip) ----
  ...pair("AMB", "base", 3500, 3500),
  ...pair("W/C", "base", 5500, 7000),
  ...pair("BWC", "base", 8500, 10000),
  ...pair("STR", "base", 13000, 19500),
  ...pair("BSTR", "base", 20000, 25000),
  // ---- Per mile (beyond the first FREE_MILES) ----
  ...pair("AMB", "per_mile", 175, 275, FREE_MILES),
  ...pair("W/C", "per_mile", 200, 275, FREE_MILES),
  ...pair("BWC", "per_mile", 225, 275, FREE_MILES),
  ...pair("STR", "per_mile", 250, 350, FREE_MILES),
  ...pair("BSTR", "per_mile", 300, 350, FREE_MILES),
];

// ---- Resolved lookup ---------------------------------------------------------

interface ResolvedRate {
  baseCents: number;
  perMileCents: number;
  freeMiles: number;
}

/** transportType -> window -> resolved base/per-mile. */
export type RateTable = Record<string, Partial<Record<RateWindow, ResolvedRate>>>;

/** Fold a flat list of rate rows into a `{type}{window}` lookup. */
export function buildRateTable(rows: RateRow[]): RateTable {
  const table: RateTable = {};
  for (const row of rows) {
    const byWindow = (table[row.transportType] ??= {});
    const slot = (byWindow[row.window] ??= {
      baseCents: 0,
      perMileCents: 0,
      freeMiles: 0,
    });
    if (row.rateType === "base") {
      slot.baseCents = row.amountCents;
    } else {
      slot.perMileCents = row.amountCents;
      slot.freeMiles = row.freeMiles;
    }
  }
  return table;
}

/** The default table built from the seeded sheet — used when no DB-loaded
 *  table is supplied (e.g. in the web bundle). */
export const DEFAULT_RATE_TABLE: RateTable = buildRateTable(RATE_SHEET);

// ---- Window + price math -----------------------------------------------------

/** Parse a wall-clock string to minutes-since-midnight, or null when it
 *  carries no usable HH:MM (e.g. "WILL_CALL", "", a free-form label). */
function toMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const m = /(\d{1,2}):(\d{2})/u.exec(time);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Decide which pricing window a pickup time falls into. Returns null
 *  when the time can't be parsed (so callers can show "—" rather than
 *  guessing). */
export function pickRateWindow(
  pickupTime: string | null | undefined,
): RateWindow | null {
  const minutes = toMinutes(pickupTime);
  if (minutes === null) return null;
  return minutes >= DAY_START_MIN && minutes < DAY_END_EXCLUSIVE_MIN
    ? "day"
    : "night";
}

/**
 * Pull "HH:MM" (24h, zero-padded) out of a display string like
 * "Jun 12, 2026 13:57" or a bare "09:00", or null when there's nothing
 * parseable.
 *
 * Two deliberate properties the TMS sync relies on:
 *   - The match is UNANCHORED, so the clock time can sit anywhere in the
 *     string (TripLink ships pickup times as "Jun 12, 2026 13:57").
 *   - On failure it returns `null` — NOT "" — so a `?? fallback` chain
 *     (e.g. `extractHm(pickupTime) ?? extractHmFromIso(pickupAtIso)`)
 *     actually falls through. An empty string is not nullish and would
 *     silently swallow the fallback.
 */
export function extractHm(value: string | null | undefined): string | null {
  const m = String(value ?? "").match(/(\d{1,2}):(\d{2})/u);
  if (!m) return null;
  const h = m[1]!.padStart(2, "0");
  return `${h}:${m[2]}`;
}

/** The pickup/appointment time fields stored on a booking row. */
export interface StorableBookingTimes {
  /** "pickup" | "appointment" | "will_call". */
  timeType: string;
  /** "HH:MM" 24h, the "WILL_CALL" placeholder, or "". */
  pickupTimeEstimated: string;
  /** "HH:MM" 24h or "". */
  appointmentTime: string;
}

/**
 * Decide whether to adopt a fresh set of pickup/appointment times for a
 * booking row the TMS sync would otherwise leave untouched — i.e. a
 * V2-form row (`triplink_synced = false`).
 *
 * The sync's authoritative-overwrite path runs only for
 * `triplink_synced = true` rows, to protect the richer V2 payload and
 * admin edits. But a Will-Call booked through our own form is stored
 * with the unpriceable `"WILL_CALL"` placeholder; once it finishes, the
 * live feed carries the real pickup time, yet nothing copies it onto the
 * row — so the completed trip stays "TBD" forever, even after a
 * Backfill 60d (the 1.19.4 recovery lived only in the synced branch).
 *
 * This recovers the real time, but ONLY when BOTH hold:
 *   - the row's CURRENTLY-stored time can't be priced (a "WILL_CALL"
 *     placeholder or blank) — so a genuine admin-entered pickup /
 *     appointment time is never clobbered, AND
 *   - the candidate derived from the feed CAN be priced.
 *
 * Returns the times to store, or `null` to leave the row unchanged.
 */
export function recoverUnpricedBookingTimes(
  stored: Pick<StorableBookingTimes, "pickupTimeEstimated" | "appointmentTime">,
  candidate: StorableBookingTimes,
): StorableBookingTimes | null {
  const storedPriceable =
    pickRateWindow(stored.pickupTimeEstimated) !== null ||
    pickRateWindow(stored.appointmentTime) !== null;
  if (storedPriceable) return null;
  const candidatePriceable =
    pickRateWindow(candidate.pickupTimeEstimated) !== null ||
    pickRateWindow(candidate.appointmentTime) !== null;
  if (!candidatePriceable) return null;
  return candidate;
}

/**
 * Resolve the billable wall-clock pickup time for a WILL-CALL trip.
 *
 * A Will-Call booking has no scheduled pickup time at booking ("patient
 * will call when ready"), so it's stored with the literal `"WILL_CALL"`
 * placeholder and can't be priced — `pickRateWindow("WILL_CALL")` is
 * null, so the whole trip shows "TBD" even when mileage is present.
 *
 * But once the patient has ACTUALLY been picked up, TMS reports the real
 * pickup instant as an absolute UTC timestamp (`pickupAtIso`). This
 * returns that instant's America/New_York "HH:MM" so the rate engine can
 * choose a day/night window and price the (now completed) trip.
 *
 * The guard that keeps not-yet-picked-up trips unpriced: we only adopt
 * the instant when it's in the PAST (the pickup has happened). A trip
 * still awaiting pickup can legitimately carry a *future* nominal
 * `pickupAtIso` (the edit-lead-time gate is anchored to it), so presence
 * alone isn't enough — a future instant returns null and the trip stays
 * "WILL_CALL" / TBD until it's genuinely been picked up. Because
 * `pickupAtIso` is an absolute instant, the past/future comparison is
 * timezone-safe. We never fabricate a time: no usable instant → null.
 */
export function willCallActualPickupHm(
  pickupAtIso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!pickupAtIso) return null;
  const t = Date.parse(pickupAtIso);
  if (!Number.isFinite(t)) return null;
  // Future instant = a nominal anchor on a trip still awaiting pickup.
  // Don't price it — leave it TBD until the pickup actually happens.
  if (t > now.getTime()) return null;
  // Format the instant as NY wall-clock "HH:MM" (24h) — the same shape
  // the rate engine's day/night split expects.
  return isoToNyHm(pickupAtIso);
}

/**
 * The America/New_York wall-clock "HH:MM" (24h) of an absolute ISO-8601
 * instant, or null when the input is absent or unparseable. Pure and
 * timezone-safe (the instant is unambiguous; we format it in NY).
 *
 * Factored out of `willCallActualPickupHm` so the Will-Call recovery and
 * the scheduled-pickup capture (`scheduledPickupHm`) format an instant the
 * exact same way. Unlike `willCallActualPickupHm` it has NO past/future
 * guard — callers that need one apply it themselves.
 */
export function isoToNyHm(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const hm = fmt.format(new Date(t)).replace(/\s+/gu, "");
  // Guard against a "24:00" rollover some engines emit at midnight.
  return /^([01]\d|2[0-3]):[0-5]\d$/u.test(hm) ? hm : null;
}

/** The pickup-instant fields the TMS feed carries for a trip. */
export interface PickupInstant {
  /** Same moment as `pickupTime` but as an absolute ISO-8601 UTC
   *  timestamp — unambiguous, so it's preferred. Absent until the feed
   *  carries one (a freshly-booked Will-Call awaiting pickup has none). */
  pickupAtIso?: string | null;
  /** Human display form ("May 13, 2026 09:00"), in NY local. Used as a
   *  fallback when no ISO instant is present. */
  pickupTime?: string | null;
}

/**
 * The TMS scheduled (and, once the patient is picked up, actual) pickup
 * wall-clock time as "HH:MM" (24h), or null when the feed carries no usable
 * pickup clock.
 *
 * Resolution order mirrors the rest of the sync: the human `pickupTime`
 * string first ("Jun 11, 2026 13:00" → "13:00", read straight off the NY
 * wall clock), then the unambiguous UTC `pickupAtIso` formatted into NY
 * time. This is the value the TripLink sync captures into
 * `bookings.scheduled_pickup_time` for ALL time types — including
 * appointment-mode trips, where the feed's real pickup is otherwise
 * discarded.
 *
 * Pure + side-effect free. The captured clock now feeds the day/night fare
 * window through `effectivePickupTime` (apps/api), which falls back to
 * `scheduled_pickup_time` when `pickup_time_estimated` has no parseable window
 * — so an appointment-mode trip prices off its real pickup instead of TBD.
 */
export function scheduledPickupHm(trip: PickupInstant): string | null {
  return extractHm(trip.pickupTime) ?? isoToNyHm(trip.pickupAtIso);
}

/** The feed fields the booking-times mapping reads. A trimmed view of
 *  `TripScheduleItem` so the mapping stays a pure, independently-testable
 *  function with no dependency on the API or the DB layer. */
export interface FeedTripTimes extends PickupInstant {
  /** Live TripLink status (decides the Will-Call branch). */
  status?: string | null;
  /** Appointment-mode display time ("May 15, 2026 14:00"), present only on
   *  appointment trips. */
  appointmentTime?: string | null;
}

/** The pickup/appointment columns the sync writes for one booking row. */
export interface ExtractedBookingTimes extends StorableBookingTimes {
  /** TMS scheduled/actual pickup "HH:MM" (24h) or "" — captured for ALL
   *  time types into `bookings.scheduled_pickup_time`. Feeds the day/night
   *  window via effectivePickupTime's fallback (appointment-mode trips). */
  scheduledPickupTime: string;
}

/**
 * Map a TripLink feed item's time fields into the
 * (time_type, pickup_time_estimated, appointment_time, scheduled_pickup_time)
 * shape the `bookings` row stores. Pure given `now`.
 *
 * The first three outputs are UNCHANGED from the historical mapping — the
 * Will-Call placeholder/recovery, the appointment-mode branch (which leaves
 * `pickup_time_estimated` ""), and the pickup-mode branch all behave exactly
 * as before, so no trip's priced window moves. The ONLY addition is
 * `scheduledPickupTime`, captured uniformly for every branch from
 * `scheduledPickupHm` so the genuine pickup window is recorded distinct from
 * the estimated pickup and the appointment time.
 */
export function extractBookingTimes(
  trip: FeedTripTimes,
  now: Date = new Date(),
): ExtractedBookingTimes {
  // Captured for EVERY branch — including appointment + will-call — so the
  // real pickup window lands on the row regardless of time type.
  const scheduledPickupTime = scheduledPickupHm(trip) ?? "";

  const isWillCall = String(trip.status ?? "").toLowerCase() === "will_call";
  if (isWillCall) {
    // Unchanged Will-Call handling: the row keeps the unpriceable
    // "WILL_CALL" placeholder until the patient has ACTUALLY been picked up
    // (a PAST pickup instant), at which point the real clock is adopted.
    const pickedUpHm = willCallActualPickupHm(trip.pickupAtIso, now);
    return {
      timeType: "will_call",
      pickupTimeEstimated: pickedUpHm ?? "WILL_CALL",
      appointmentTime: "",
      scheduledPickupTime,
    };
  }

  // Unchanged appointment-mode handling: store ONLY the appointment time;
  // pickup_time_estimated stays "". (The scheduled pickup is now also
  // recorded separately, above, but does not change this.)
  const apptHm = extractHm(trip.appointmentTime ?? "");
  if (apptHm) {
    return {
      timeType: "appointment",
      pickupTimeEstimated: "",
      appointmentTime: apptHm,
      scheduledPickupTime,
    };
  }

  // Unchanged pickup-mode handling: pickup_time_estimated is the scheduled
  // pickup clock (which equals scheduledPickupTime here).
  return {
    timeType: "pickup",
    pickupTimeEstimated: scheduledPickupHm(trip) ?? "",
    appointmentTime: "",
    scheduledPickupTime,
  };
}

/**
 * The America/New_York calendar date (`YYYY-MM-DD`) a trip's pickup
 * instant falls on — i.e. the *service date* the trip belongs to in every
 * report, billing window, and date-range filter.
 *
 * Resolution order mirrors the rest of the sync: the unambiguous UTC
 * `pickupAtIso` first, then the human `pickupTime` string. Returns `null`
 * only when NEITHER yields a parseable instant — which is exactly the
 * Will-Call / awaiting-pickup case (no scheduled pickup yet), where a
 * freshly-booked trip legitimately has no service date until it's picked
 * up and TMS reports a real instant.
 *
 * NY-anchored on purpose: the API container runs in UTC, and a naive
 * `toISOString().slice(0,10)` flips to "tomorrow" from 8 PM ET onward,
 * which would file an evening pickup under the wrong calendar day.
 */
export function serviceDateOfPickup(trip: PickupInstant): string | null {
  if (trip.pickupAtIso) {
    const d = new Date(trip.pickupAtIso);
    if (!Number.isNaN(d.getTime())) return nyCalendarDate(d);
  }
  if (trip.pickupTime) {
    // `pickupTime` is ALREADY a NY wall-clock string ("Jun 4, 2026 14:10"),
    // so its calendar date is simply the date written in the string. The
    // previous `nyCalendarDate(new Date(pickupTime))` round-tripped it
    // through UTC — the API container runs in UTC, so `new Date(...)` read
    // the wall clock AS UTC and the NY conversion then subtracted the ET
    // offset, flipping an early-morning pickup (00:00–03:59 ET) back to the
    // PREVIOUS calendar day. That mis-stamped transport_date one day early
    // and could drop the trip out of a report/date-range window. Parse the
    // date out of the string directly so there's no zone conversion at all.
    const fromWallClock = nyDateFromWallClock(trip.pickupTime);
    if (fromWallClock) return fromWallClock;
    // Last-ditch: an unexpected format that still parses as an instant.
    const d = new Date(trip.pickupTime);
    if (!Number.isNaN(d.getTime())) return nyCalendarDate(d);
  }
  return null;
}

/**
 * Decide the `transport_date` to WRITE when re-syncing an EXISTING
 * booking row, given the date currently stored on it and the trip's
 * latest pickup instant from the feed.
 *
 * Returns the freshly-derived service date ONLY when it both resolves to
 * a real (non-null) date AND differs from what's stored; otherwise
 * `null`, meaning "leave the stored value untouched".
 *
 * Why this exists. A trip first synced as Will-Call has no pickup instant,
 * so it lands with `transport_date = null` (or a stale nominal date). The
 * insert path can't do better. Once the trip completes, TMS reports the
 * real pickup instant — but the update path only ever set
 * `transport_date` inside the `triplink_synced` branch, so form-booked
 * Will-Calls (and any row whose date was never recomputed in-window)
 * stayed null forever. A null `transport_date` is silently dropped by
 * every `transport_date BETWEEN …` filter, so the completed trip vanishes
 * from the Bookings date-range view, the Reports window, and the CSV
 * export even though its real service date is in range.
 *
 * Two guards make the refresh safe:
 *   - non-null only: a feed tick that momentarily lacks a pickup instant
 *     must never null out a good stored date, and
 *   - differs only: don't rewrite the column (or churn an identical
 *     form-picked date) when nothing changed.
 */
export function nextTransportDate(
  storedTransportDate: string | null | undefined,
  trip: PickupInstant,
): string | null {
  const derived = serviceDateOfPickup(trip);
  if (derived && derived !== storedTransportDate) return derived;
  return null;
}

/**
 * Decide the `canceled_at` value to WRITE for an EXISTING booking row on
 * re-sync, so the flag always tracks the trip's CURRENT status.
 *
 * `canceled_at` is a cancellation FLAG, not history. A trip can briefly
 * pass through a cancellation (canceled_at gets stamped) and then complete
 * to `finished` / `no_show`; if the stamp is never cleared, the trip is
 * wrongly treated as cancelled — dropped by the Bookings "Hide cancelled"
 * filter and miscounted in the cancellation KPIs. So the flag must follow
 * the current status:
 *
 *   - currently cancelled, nothing stored  → stamp `now` (first observation)
 *   - currently cancelled, already stamped → leave it (preserve the
 *                                            original cancellation time)
 *   - NOT currently cancelled, a stamp set → CLEAR it (stale flag)
 *   - NOT currently cancelled, nothing set → leave it (already correct)
 *
 * Returns the value to store (`Date` to stamp, `null` to clear) or
 * `undefined` to leave the column untouched. The clear path only fires
 * when the current status is NOT a cancellation, so a genuine cancellation
 * (whose status still reads *cancel*) is never clobbered.
 */
export function nextCanceledAt(
  storedCanceledAt: Date | null | undefined,
  isCancelled: boolean,
  now: Date,
): Date | null | undefined {
  if (isCancelled) {
    return storedCanceledAt ? undefined : now;
  }
  return storedCanceledAt ? null : undefined;
}

/**
 * Map TripLink's service-type label ("MS-Wheelchair", "MS-Bariatric
 * Wheelchair", …) to the short transport code the rest of the app + the
 * ratesheet use (AMB | W/C | BWC | STR | BSTR).
 *
 * Returns `null` — NOT a fallback — when the label is empty, unknown, or
 * doesn't match any known level of service. This is the SAFE mapping the
 * re-sync update path needs: an unrecognised value must never be coerced
 * into a billable code (the old insert-only mapping defaulted unknowns to
 * "AMB", which is fine for a brand-new row that has nothing better, but
 * would silently downgrade a good code if written blindly on every
 * re-sync). The insert path keeps the historical behaviour by applying
 * its own `?? "AMB"` fallback on top of this.
 *
 * The match order matters: the more specific "bariatric …" variants are
 * tested before the plain wheelchair / stretcher patterns so
 * "MS-Bariatric Wheelchair" resolves to BWC, not W/C.
 */
export function serviceTypeToTransportCode(
  label: string | null | undefined,
): "AMB" | "W/C" | "BWC" | "STR" | "BSTR" | null {
  const s = String(label ?? "")
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (/bariatric.*wheel.*chair/u.test(s)) return "BWC";
  if (/bariatric.*stretcher/u.test(s)) return "BSTR";
  if (/wheel.*chair|w\/?c/u.test(s)) return "W/C";
  if (/stretcher|^str$/u.test(s)) return "STR";
  if (/ambulatory|ambulance|^amb$/u.test(s)) return "AMB";
  return null;
}

/** The display LOS labels keyed by our short transport code — the inverse
 *  of {@link serviceTypeToTransportCode}. These are the exact "MS-…"
 *  level-of-service strings TripLink uses (and that the external invoice
 *  Excel prints in its LOS column). */
const TRANSPORT_CODE_TO_SERVICE_LABEL: Record<string, string> = {
  AMB: "MS-Ambulatory",
  "W/C": "MS-Wheelchair",
  BWC: "MS-Bariatric Wheelchair",
  STR: "MS-Stretcher",
  BSTR: "MS-Bariatric Stretcher",
};

/**
 * Map our short transport code (AMB | W/C | BWC | STR | BSTR) back to the
 * TripLink "MS-…" level-of-service label — the inverse of
 * `serviceTypeToTransportCode`, used to print the LOS column on a generated
 * invoice so it matches the format the external n8n workflow produced.
 *
 * Returns `null` — NOT a fallback — when the code is empty or unrecognised,
 * mirroring `serviceTypeToTransportCode`'s "never coerce an unknown into a
 * billable label" contract. The match is case-insensitive and trims
 * surrounding whitespace; the slash in "W/C" is preserved by upper-casing.
 */
export function transportCodeToServiceLabel(
  code: string | null | undefined,
): string | null {
  const c = String(code ?? "")
    .trim()
    .toUpperCase();
  if (!c) return null;
  return TRANSPORT_CODE_TO_SERVICE_LABEL[c] ?? null;
}

/**
 * Decide the `transport_type` (level of service) to WRITE for an EXISTING
 * booking row on re-sync, so the billed LOS always tracks what dispatch
 * currently has in TripLink.
 *
 * The bug this closes: `transport_type` was derived ONLY on the INSERT
 * path. When a dispatcher changed a trip's service type in TripLink AFTER
 * booking (e.g. MS-Wheelchair → MS-Bariatric Wheelchair), the portal kept
 * the stale code and billed the wrong rate, because the update path never
 * recomputed it.
 *
 * The policy mirrors `nextCanceledAt` / `nextTransportDate`:
 *   - incoming serviceType maps to a KNOWN LOS that DIFFERS from stored
 *                                          → return the new code (write it)
 *   - incoming serviceType maps to the SAME code as stored → `undefined`
 *                                          (no-op — don't churn the column)
 *   - incoming serviceType is empty / unknown / unmapped   → `undefined`
 *                                          (leave the existing code alone —
 *                                          NEVER downgrade a good code to a
 *                                          fallback like "AMB")
 *
 * Returning `undefined` means "leave the column untouched", so a non-
 * `undefined` result always signals a real, billable LOS change — which
 * the caller also uses to drive the post-completion audit trail.
 */
export function nextTransportType(
  storedCode: string | null | undefined,
  incomingServiceType: string | null | undefined,
): string | undefined {
  const mapped = serviceTypeToTransportCode(incomingServiceType);
  if (!mapped) return undefined;
  if (mapped === storedCode) return undefined;
  return mapped;
}

/** America/New_York calendar date of an instant as `YYYY-MM-DD`.
 *  en-CA yields the ISO shape directly. Mirrors the API's `nyDateIso`
 *  so the sync and the shared layer agree on a trip's service date. */
function nyCalendarDate(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

const WALL_CLOCK_MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

/** Pull the `YYYY-MM-DD` calendar date out of TripLink's NY wall-clock
 *  pickup string ("Jun 4, 2026 14:10" / "May 13, 2026 09:00"). Because the
 *  string is ALREADY local, the date is read straight off it with no
 *  timezone conversion — that's the whole point: round-tripping it through
 *  `new Date()` (parsed as UTC on the UTC API container) then back to ET
 *  shifts an early-morning pickup to the day before. Returns null when the
 *  leading "Mon D, YYYY" can't be parsed so the caller can fall back. */
function nyDateFromWallClock(value: string): string | null {
  const m = value
    .trim()
    .match(/^([A-Za-z]{3,9})\.?\s+(\d{1,2}),\s*(\d{4})\b/u);
  if (!m) return null;
  const month = WALL_CLOCK_MONTHS[m[1]!.slice(0, 3).toLowerCase()];
  if (!month) return null;
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (day < 1 || day > 31) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export interface ComputeTripRateInput {
  /** Our transport code (AMB | W/C | BWC | STR | BSTR). */
  transportType: string;
  /** Trip distance in miles. Null/undefined → not priceable. */
  mileage: number | null | undefined;
  /** Pickup wall-clock time ("HH:MM"); decides day vs night. */
  pickupTime: string | null | undefined;
  /** Optional override table (DB-loaded). Defaults to the seeded sheet. */
  table?: RateTable;
  /** Window to use ONLY when `pickupTime` yields no parseable window —
   *  the day/night decision still prefers the real clock time. Lets a
   *  terminal trip whose pickup clock is unknown (a Will-Call no-show)
   *  price by mileage off a default window instead of returning null.
   *  Omit/null = no fallback (the historical behaviour: no clock →
   *  null). */
  fallbackWindow?: RateWindow | null;
}

/** The component lines of one computed fare — lets UIs render the
 *  "base + mileage" math instead of a single opaque number. */
export interface TripRateParts {
  baseCents: number;
  /** The per-mile portion: perMile × billableMiles (0 within the free
   *  15 miles). */
  mileageCents: number;
  billableMiles: number;
  freeMiles: number;
  totalCents: number;
}

/**
 * Price a single trip leg with the component breakdown, or null when it
 * can't be priced (missing mileage, unparseable pickup time, or an
 * unknown transport type). Never throws.
 */
export function computeTripRateParts(
  input: ComputeTripRateInput,
): TripRateParts | null {
  const { transportType, mileage, pickupTime } = input;
  const table = input.table ?? DEFAULT_RATE_TABLE;

  if (mileage === null || mileage === undefined) return null;
  if (!Number.isFinite(mileage) || mileage < 0) return null;

  // Prefer the real pickup clock; only fall back to a caller-supplied
  // window (e.g. the day default a no-show uses) when the clock can't be
  // parsed. Mileage is still required above, so a no-show with NULL
  // mileage stays null even with a fallback window.
  const window = pickRateWindow(pickupTime) ?? input.fallbackWindow ?? null;
  if (!window) return null;

  const rate = table[transportType]?.[window];
  if (!rate) return null;

  // Strip float-subtraction noise (15.1 − 15 = 0.09999…964) before the
  // per-mile multiply, otherwise 0.1 billable miles × 175¢ computes as
  // 17.4999…¢ and mis-rounds to 17¢ instead of 18¢. Mileage arrives with
  // at most a couple of decimals, so snapping to 1e-6 miles is lossless.
  const billableMiles =
    Math.round(Math.max(0, mileage - rate.freeMiles) * 1e6) / 1e6;
  const mileageCents = Math.round(rate.perMileCents * billableMiles);
  return {
    baseCents: rate.baseCents,
    mileageCents,
    billableMiles,
    freeMiles: rate.freeMiles,
    totalCents: rate.baseCents + mileageCents,
  };
}

// ---- Per-trip charges (fare + fees) -------------------------------------------

/** Default IT fee in cents — added to every trip's fare. The live value
 *  is seeded into `transport_rates` (rate_type='fee') so it can change
 *  without a deploy; this constant is the code fallback. */
export const IT_FEE_CENTS = 150;

/** The billing breakdown for one trip. Kept as separate lines (not a
 *  single number) per the billing rules:
 *    - normal / finished / NO-SHOW trips: fare + IT fee.
 *    - CANCELLED trips: IT fee only (fare = $0.00).
 *  Future fee lines slot in here without touching consumers again. */
export interface TripCharges {
  /** The transport fare (base + per-mile). 0 for cancelled trips;
   *  null when the trip can't be priced yet. */
  fareCents: number | null;
  /** Base-fare component of the fare. Null when the fare is null or
   *  the trip is cancelled (no fare lines — IT fee only). */
  fareBaseCents: number | null;
  /** Per-mile component of the fare (0 within the free 15 miles).
   *  Null in the same cases as `fareBaseCents`. */
  fareMileageCents: number | null;
  /** Flat IT fee — always charged, including on cancellations. */
  itFeeCents: number;
  /** fare + all fees. Null when the fare is null (can't total an
   *  unpriceable live trip); always present for cancelled trips. */
  totalCents: number | null;
}

export interface ComputeTripChargesInput extends ComputeTripRateInput {
  /** Cancelled trips bill the IT fee only. */
  cancelled?: boolean;
  /** Override for the seeded IT fee (DB-loaded). */
  itFeeCents?: number;
  /** Hand-set fare in cents (a Royal super-admin's per-leg override,
   *  e.g. to bill deadhead miles after a long discharge run). When set
   *  on a live trip it REPLACES the computed fare wholesale — no
   *  base/per-mile breakdown — and the IT fee is still added on top.
   *  Cancellation wins: a cancelled leg bills the IT fee only, override
   *  or not. Null/undefined = no override (normal computed pricing). */
  fareOverrideCents?: number | null;
  /** True when the trip is a no-show. A no-show is terminal and bills the
   *  normal mileage-based fare (base + per-mile), exactly like a completed
   *  trip — so it must NEVER sit in TBD when mileage is present. When the
   *  pickup clock yields no day/night window (e.g. a Will-Call no-show
   *  that never got a scheduled pickup time), the fare engine defaults to
   *  the **day** window so the mileage-based fare still computes. This
   *  does NOT fabricate a stored pickup time and does NOT affect any
   *  non-no-show trip: a finished/awaiting trip with no window prices as
   *  before (null/TBD). A cancellation or a hand-set override still wins
   *  over this (both are handled before pricing below). */
  noShow?: boolean;
}

/** Full billing breakdown for one trip. Never throws. */
export function computeTripCharges(
  input: ComputeTripChargesInput,
): TripCharges {
  const itFeeCents = input.itFeeCents ?? IT_FEE_CENTS;
  if (input.cancelled) {
    // Cancellation policy: the ride is not billed, the IT fee is.
    // This intentionally ignores any fare override.
    return {
      fareCents: 0,
      fareBaseCents: null,
      fareMileageCents: null,
      itFeeCents,
      totalCents: itFeeCents,
    };
  }
  // Manual fare override — the hand-set number IS the fare. Negative or
  // non-finite values are treated as "no override" (the API rejects
  // them before they can be stored; this guard just keeps the engine
  // total-safe against bad rows). Component lines are null: a hand-set
  // number has no base/per-mile decomposition.
  const override = input.fareOverrideCents;
  if (override != null && Number.isFinite(override) && override >= 0) {
    const fareCents = Math.round(override);
    return {
      fareCents,
      fareBaseCents: null,
      fareMileageCents: null,
      itFeeCents,
      totalCents: fareCents + itFeeCents,
    };
  }
  // No-shows intentionally take this path too — full price. A no-show is
  // terminal and always prices by mileage, so when its pickup clock can't
  // resolve a window we default to the day window rather than returning a
  // null (TBD) fare. Non-no-show trips get no fallback, so their pricing
  // is unchanged. (Mileage is still required: a no-show with NULL mileage
  // remains null below.)
  const parts = computeTripRateParts(
    input.noShow ? { ...input, fallbackWindow: input.fallbackWindow ?? "day" } : input,
  );
  return {
    fareCents: parts?.totalCents ?? null,
    fareBaseCents: parts?.baseCents ?? null,
    fareMileageCents: parts?.mileageCents ?? null,
    itFeeCents,
    totalCents: parts !== null ? parts.totalCents + itFeeCents : null,
  };
}

// ---- Cancellation billing classification ------------------------------------

/**
 * How a trip's TripLink status maps to billing — the single notion the
 * dashboard tiles, report spend, and CSV exports all classify through so
 * they agree to the row:
 *   - "full_fare": finished / no-show / live → fare + IT fee.
 *   - "fee_only":  a billable cancellation → IT fee only, $0 fare.
 *   - "excluded":  not billed at all and dropped from every billing count
 *                  (deleted tombstones + non-billable cancellations).
 */
export type TripBillingClass = "full_fare" | "fee_only" | "excluded";

/**
 * The TripLink statuses that bill the IT fee on a cancellation (fare $0).
 *
 * These are the REAL status strings TripLink stores (the sync writes
 * `trip.status` verbatim — see `triplinkSync.ts`), confirmed against a live
 * query of `bookings.triplink_status`. The 1.19.2/1.19.3 names
 * (`member_canceled`, `br_canceled`, `co_canceled`) never existed in the
 * data, so the old exact-match billed ZERO cancellations and every cancel
 * fell through to "excluded" — the v1.19.4 regression. Per the product
 * owner, the customer/member-side cancellations below bill the $1.50 IT fee;
 * fleet/organization cancellations (and any other cancel) bill nothing.
 */
const FEE_ONLY_CANCEL_STATUSES: ReadonlySet<string> = new Set([
  "customer_canceled",
  "customer_requested_cancel",
  "late_cancel",
]);

/**
 * Cancellation statuses that bill NOTHING and drop out of every billing
 * count, alongside the `deleted` tombstone and the generic "anything else
 * containing 'cancel'" catch-all. Listed explicitly for clarity even though
 * the catch-all would also cover them.
 */
const EXCLUDED_CANCEL_STATUSES: ReadonlySet<string> = new Set([
  "fleet_canceled",
  "organization_canceled",
]);

/**
 * Statuses that are DEFINITIVELY not a cancellation — the terminal
 * outcomes (finished / no_show) plus the live / in-progress / pre-trip
 * states a trip passes through on the way there. When the CURRENT status
 * is one of these, the trip is billed normally (full fare) regardless of
 * any `canceled_at` stamp: a trip can briefly pass through a cancellation
 * (canceled_at gets stamped) and then complete to `finished`, and until
 * the sync clears that stamp it would otherwise be wrongly excluded. The
 * STATUS is the source of truth, not a stale flag.
 */
const NON_CANCELLATION_STATUSES: ReadonlySet<string> = new Set([
  "finished",
  "no_show",
  "at_destination",
  "assigned",
  "en_route",
  "in_progress",
  "will_call",
  "available",
  "pending",
  "draft",
]);

/**
 * Classify one trip's billing treatment from its TripLink status and
 * whether it carries a `canceled_at` timestamp. Centralizes the rule so
 * every billing surface agrees to the row.
 *
 * Cancellation is determined by the trip's CURRENT status, never by a
 * stale `canceled_at` alone — a finished/no_show/live trip that briefly
 * passed through a cancellation is NOT cancelled (see
 * `NON_CANCELLATION_STATUSES`). The `hasCanceledAt` flag only acts as a
 * last-ditch signal for a genuinely unknown/ambiguous status.
 *
 * The rule (rule-based, so any unknown future cancel defaults to excluded
 * rather than being billed full fare):
 *   - customer_canceled / customer_requested_cancel / → "fee_only"
 *     late_cancel                                        (IT fee only).
 *   - `deleted`, fleet_canceled, organization_canceled, → "excluded"
 *     or any OTHER status containing "cancel"               (bill nothing,
 *                                                            drop from every
 *                                                            count).
 *   - finished / no_show / live (NON_CANCELLATION_STATUSES) → "full_fare"
 *     EVEN with a stale `canceled_at`.
 *   - an unknown/ambiguous status carrying a `canceled_at` → "excluded".
 *   - everything else → "full_fare".
 */
export function classifyTripBilling(
  triplinkStatus: string | null | undefined,
  hasCanceledAt: boolean,
): TripBillingClass {
  const status = String(triplinkStatus ?? "").toLowerCase();
  if (FEE_ONLY_CANCEL_STATUSES.has(status)) return "fee_only";
  if (
    status === "deleted" ||
    EXCLUDED_CANCEL_STATUSES.has(status) ||
    status.includes("cancel")
  ) {
    return "excluded";
  }
  // A recognized non-cancellation status wins outright — a stale
  // `canceled_at` on a finished/no_show/live trip must NOT exclude it.
  if (NON_CANCELLATION_STATUSES.has(status)) return "full_fare";
  // Only a genuinely unknown/ambiguous status falls back to `canceled_at`
  // as a cancellation signal.
  if (hasCanceledAt) return "excluded";
  return "full_fare";
}

/**
 * True when a status bills nothing and drops out of all billing counts —
 * the status-only view of the `classifyTripBilling` rule (every `deleted`
 * tombstone or non-billable cancellation). Used by call sites that gate on
 * the status before pricing; `classifyTripBilling` remains the single
 * source of truth.
 */
export function isExcludedFromBilling(
  triplinkStatus: string | null | undefined,
): boolean {
  const status = String(triplinkStatus ?? "").toLowerCase();
  if (FEE_ONLY_CANCEL_STATUSES.has(status)) return false;
  return (
    status === "deleted" ||
    EXCLUDED_CANCEL_STATUSES.has(status) ||
    status.includes("cancel")
  );
}

/**
 * True when a trip's TripLink status is a no-show. A no-show is a
 * terminal, full-fare outcome (the ride was dispatched and mileage is
 * known, the patient just wasn't there) — so callers price it like a
 * completed trip and pass `noShow` into the fare engine, which guarantees
 * it prices by mileage instead of sitting in TBD when the pickup clock is
 * unknown. The status string is written verbatim from the feed (see
 * `triplinkSync.ts`); the comparison is lowercase-safe.
 */
export function isNoShowStatus(
  triplinkStatus: string | null | undefined,
): boolean {
  return String(triplinkStatus ?? "").toLowerCase() === "no_show";
}

/** Format integer cents as a US dollar string: 123456 -> "$1,234.56". */
export function formatUsdCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return "—";
  }
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}
